'use client';
import { useEffect, useState } from 'react';

import useHash from '@/hooks/useHash';

import { Modal } from '@/components/molecules/modal';

import { CONTACT_SALES_URL_HASH } from '@/constant/urls';
import { ContactSalesBookingForm } from '@/features/modals/ContactSales/ContactSalesBookingForm';
import { ContactSalesForm } from '@/features/modals/ContactSales/ContactSalesForm';
import { ContactSalesFormError } from '@/features/modals/ContactSales/ContactSalesFormError';
import { ContactSalesFormSuccess } from '@/features/modals/ContactSales/ContactSalesFormSuccess';
import { useTranslation } from '@/lib/i18n/i18n.client';

export type ContactSalesModalStep =
  | 'form_page'
  | 'success_page'
  | 'error_page'
  | 'booking_page';

export function ContactSalesModal() {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [step, setStep] = useState<ContactSalesModalStep>('form_page');
  const { hash, clearHash } = useHash();

  const { lang } = useTranslation();

  useEffect(() => {
    setIsModalVisible(hash === CONTACT_SALES_URL_HASH);
  }, [hash]);

  function handleClose() {
    // showChatBubble();
    setIsModalVisible(false);
    clearHash();
    setStep('form_page');
  }

  function handleBooking() {
    setStep('booking_page');
  }

  function handleSuccess() {
    setStep('success_page');
  }

  function handleTryAgain() {
    setStep('form_page');
  }

  function handleError() {
    setStep('error_page');
  }

  return (
    <div data-tracker-section='contact-sales'>
      <Modal
        isOpen={isModalVisible}
        onClose={handleClose}
        fullSizeMobile
        closeOnOutsideClick={false}
        classNames={{
          root: 'items-start md:items-center top-12 md:top-0',
          modal: `max-section-width md:p-[3.75rem] w-full px-5 py-12 max-h-[100%] md:max-h-[94%] rounded-none md:rounded-xl`,
          content: 'md:h-full h-fit',
        }}
        dataTrackerSection='contact-sales'
      >
        {step === 'form_page' && (
          <ContactSalesForm onSuccess={handleSuccess} onError={handleError} />
        )}
        {step === 'success_page' && (
          <ContactSalesFormSuccess
            onSubmit={lang === 'en' ? handleBooking : undefined}
            onClose={lang !== 'en' ? handleClose : undefined}
          />
        )}
        {step === 'error_page' && (
          <ContactSalesFormError onSubmit={handleTryAgain} />
        )}
        {step === 'booking_page' && <ContactSalesBookingForm />}
      </Modal>
    </div>
  );
}
